import React, { useState } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { Search, Shield, Star, CreditCard } from 'lucide-react';

const BuyerGuide = () => {
  const { themeColors } = useTheme();
  const [expandedCategory, setExpandedCategory] = useState(null);

  const containerStyle = {
    minHeight: 'calc(100vh - 120px)',
    backgroundColor: themeColors.background,
    padding: '40px 20px'
  };

  const contentStyle = {
    maxWidth: '1000px',
    margin: '0 auto'
  };

  const headerStyle = {
    textAlign: 'center',
    marginBottom: '60px'
  };

  const titleStyle = {
    color: themeColors.text,
    fontSize: '48px',
    fontWeight: 'bold',
    marginBottom: '20px'
  };

  const subtitleStyle = {
    color: themeColors.textSecondary,
    fontSize: '20px',
    lineHeight: '1.6',
    maxWidth: '700px',
    margin: '0 auto'
  };

  const sectionStyle = {
    marginBottom: '60px'
  };

  const sectionTitleStyle = {
    color: themeColors.text,
    fontSize: '24px',
    fontWeight: 'bold',
    marginBottom: '24px'
  };

  const stepCardStyle = {
    backgroundColor: themeColors.cardBackground,
    borderRadius: '12px',
    padding: '24px',
    marginBottom: '20px',
    borderLeft: `4px solid ${themeColors.primary}`,
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.08)'
  };

  const stepNumberStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    backgroundColor: themeColors.primary,
    color: '#ffffff',
    fontWeight: 'bold',
    marginRight: '12px',
    flexShrink: 0
  };

  const stepTitleStyle = {
    color: themeColors.text,
    fontSize: '18px',
    fontWeight: 'bold',
    marginBottom: '12px',
    display: 'flex',
    alignItems: 'center'
  };

  const cardStyle = {
    backgroundColor: themeColors.cardBackground,
    borderRadius: '16px',
    padding: '32px',
    marginBottom: '24px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
  };

  const accentCardStyle = {
    ...cardStyle,
    background: `linear-gradient(135deg, ${themeColors.primary}10 0%, ${themeColors.accent}10 100%)`,
    border: `2px solid ${themeColors.primary}30`
  };

  const paragraphStyle = {
    color: themeColors.textSecondary,
    fontSize: '16px',
    lineHeight: '1.8',
    marginBottom: '16px'
  };

  const listStyle = {
    color: themeColors.textSecondary,
    fontSize: '16px',
    lineHeight: '2',
    paddingLeft: '20px',
    marginBottom: '16px'
  };

  const iconTextStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    color: themeColors.text,
    fontSize: '20px',
    fontWeight: '600',
    marginBottom: '16px'
  };

  const categoryHeaderStyle = {
    backgroundColor: themeColors.cardBackground,
    borderRadius: '12px',
    padding: '20px',
    marginBottom: '16px',
    cursor: 'pointer',
    border: `1px solid ${themeColors.border}`,
    transition: 'box-shadow 0.2s'
  };

  const categoryContentStyle = {
    backgroundColor: themeColors.background,
    borderRadius: '8px',
    padding: '20px',
    marginTop: '12px'
  };

  const categories = [
    {
      id: 'clothing',
      title: 'Clothing & Shoes',
      icon: '👗',
      tips: [
        'Check for stains, holes, pilling, and stretched elastic',
        'Verify the size matches the listing',
        'Ask if items are from a smoke-free/pet-free home',
        'Bundle deals are often the best value for baby clothes'
      ]
    },
    {
      id: 'strollers',
      title: 'Strollers & Car Seats',
      icon: '🚗',
      tips: [
        'Ask for the manufacture date and model number',
        'Check the CPSC recall database before purchasing',
        'Car seats expire (usually 6-10 years) — verify expiration',
        'Never buy a car seat that has been in an accident',
        'Ask about all included accessories (rain cover, cup holder, etc.)'
      ]
    },
    {
      id: 'furniture',
      title: 'Cribs & Nursery Furniture',
      icon: '🛏️',
      tips: [
        'Ensure cribs meet current CPSC safety standards',
        'Drop-side cribs are banned — do not purchase',
        'Check that all hardware and assembly parts are included',
        'Ask for dimensions to ensure it fits your space'
      ]
    },
    {
      id: 'toys',
      title: 'Toys & Activity Items',
      icon: '🧸',
      tips: [
        'Check age recommendations on the toy',
        'Look for small parts that could be choking hazards for younger children',
        'Battery-operated toys — ask if batteries are included and if the item works',
        'Check for any sharp edges or broken pieces'
      ]
    }
  ];

  return (
    <div style={containerStyle}>
      <div style={contentStyle}>
        {/* Header */}
        <div style={headerStyle}>
          <h1 style={titleStyle}>Buyer Guide</h1>
          <p style={subtitleStyle}>
            Shop smart, save big, and find amazing pre-loved baby items. Here's everything you need to know about buying on BabyResell.
          </p>
        </div>

        {/* How Buying Works */}
        <div style={sectionStyle}>
          <h2 style={sectionTitleStyle}>How Buying Works</h2>

          <div style={stepCardStyle}>
            <div style={stepTitleStyle}>
              <div style={stepNumberStyle}>1</div>
              Browse & Discover
            </div>
            <p style={paragraphStyle}>
              Explore the Pinterest-style grid on the homepage, or use the search bar and filters to find exactly what you need. Filter by category, age group, condition, price range, and more. Save items you love by clicking the heart icon.
            </p>
          </div>

          <div style={stepCardStyle}>
            <div style={stepTitleStyle}>
              <div style={stepNumberStyle}>2</div>
              Check the Details
            </div>
            <p style={paragraphStyle}>
              Click on any item to see the full listing — multiple photos, detailed description, condition rating, seller info, and reviews. Don't hesitate to message the seller if you have questions.
            </p>
          </div>

          <div style={stepCardStyle}>
            <div style={stepTitleStyle}>
              <div style={stepNumberStyle}>3</div>
              Purchase Securely
            </div>
            <p style={paragraphStyle}>
              When you're ready, click 'Buy Now.' Enter your shipping address and payment details. Your payment is held securely in escrow — the seller doesn't receive payment until you confirm the item arrived as described.
            </p>
          </div>

          <div style={stepCardStyle}>
            <div style={stepTitleStyle}>
              <div style={stepNumberStyle}>4</div>
              Receive & Confirm
            </div>
            <p style={paragraphStyle}>
              Once your item arrives, inspect it carefully. If everything looks good, confirm receipt. If there's an issue, you can open a dispute within 48 hours. After confirmation, the seller gets paid and you'll be prompted to leave a review.
            </p>
          </div>
        </div>

        {/* Smart Shopping Tips */}
        <div style={sectionStyle}>
          <h2 style={sectionTitleStyle}>Smart Shopping Tips</h2>
          <div style={cardStyle}>
            <div style={iconTextStyle}>
              <Search size={24} color={themeColors.primary} />
              Shop Like a Pro
            </div>
            <ul style={listStyle}>
              <li><strong>Check seller ratings</strong> — Sellers with high ratings and multiple reviews are your safest bet</li>
              <li><strong>Read descriptions carefully</strong> — Pay attention to condition notes and what's included</li>
              <li><strong>Ask questions first</strong> — Use the messaging system to clarify anything before buying</li>
              <li><strong>Compare prices</strong> — Search for the same item across listings to find the best deal</li>
              <li><strong>Check for recalls</strong> — Verify the item hasn't been recalled at cpsc.gov, especially for car seats, cribs, and strollers</li>
              <li><strong>Look at all photos</strong> — Zoom in on details and look for any wear or damage</li>
              <li><strong>Save items to compare</strong> — Use the heart/save feature to bookmark items while you shop around</li>
            </ul>
          </div>
        </div>

        {/* Buyer Protection */}
        <div style={sectionStyle}>
          <h2 style={sectionTitleStyle}>Buyer Protection</h2>
          <div style={accentCardStyle}>
            <div style={iconTextStyle}>
              <Shield size={24} color={themeColors.primary} />
              Your money is protected on every purchase
            </div>
            <ul style={listStyle}>
              <li><strong>Escrow payments</strong> — Your payment is held securely until you confirm the item arrived</li>
              <li><strong>48-hour review window</strong> — You have 48 hours after delivery to inspect the item and report any issues</li>
              <li><strong>Dispute resolution</strong> — If an item isn't as described, our team will mediate and work toward a fair outcome</li>
              <li><strong>Secure payments</strong> — All transactions are processed through Stripe with bank-level encryption</li>
              <li><strong>No payment outside the platform</strong> — Never pay a seller through Venmo, Zelle, or cash outside BabyResell. Our buyer protection only covers in-platform transactions.</li>
            </ul>
          </div>
        </div>

        {/* What to Look For by Category */}
        <div style={sectionStyle}>
          <h2 style={sectionTitleStyle}>What to Look For by Category</h2>
          {categories.map((category) => (
            <div key={category.id}>
              <div
                style={categoryHeaderStyle}
                onClick={() => setExpandedCategory(expandedCategory === category.id ? null : category.id)}
                onMouseOver={(e) => {
                  e.currentTarget.style.boxShadow = '0 2px 12px rgba(0, 0, 0, 0.1)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <span style={{ fontSize: '24px' }}>{category.icon}</span>
                    <span style={{ color: themeColors.text, fontSize: '18px', fontWeight: '600' }}>
                      {category.title}
                    </span>
                  </div>
                  <span style={{ color: themeColors.textSecondary }}>
                    {expandedCategory === category.id ? '▲' : '▼'}
                  </span>
                </div>
              </div>
              {expandedCategory === category.id && (
                <div style={categoryContentStyle}>
                  <ul style={listStyle}>
                    {category.tips.map((tip, index) => (
                      <li key={index}>{tip}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Payment Information */}
        <div style={sectionStyle}>
          <h2 style={sectionTitleStyle}>Payment Information</h2>
          <div style={cardStyle}>
            <div style={iconTextStyle}>
              <CreditCard size={24} color={themeColors.primary} />
              How Payment Works
            </div>
            <ul style={listStyle}>
              <li>We accept Visa, Mastercard, American Express, and Discover</li>
              <li>Apple Pay and Google Pay coming soon</li>
              <li>All payments are processed through Stripe (PCI DSS compliant)</li>
              <li>You'll receive an email confirmation after every purchase</li>
              <li>Refunds are processed to your original payment method within 5-10 business days</li>
            </ul>
          </div>
        </div>

        {/* Leaving Reviews */}
        <div style={sectionStyle}>
          <h2 style={sectionTitleStyle}>Leaving Reviews</h2>
          <div style={cardStyle}>
            <div style={iconTextStyle}>
              <Star size={24} color={themeColors.primary} />
              Reviews help the whole community!
            </div>
            <p style={paragraphStyle}>
              After you receive an item, you'll be prompted to leave a rating (1-5 stars) and a written review.
            </p>
            <p style={{ ...paragraphStyle, fontWeight: '600', marginBottom: '12px' }}>
              Tips for helpful reviews:
            </p>
            <ul style={listStyle}>
              <li>Was the item as described?</li>
              <li>How was the packaging and shipping speed?</li>
              <li>How was communication with the seller?</li>
              <li>Would you buy from this seller again?</li>
              <li>Be fair and constructive — remember, sellers are parents too!</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BuyerGuide;
